/**
 * CustomQuestionField - Renders a single custom booking question with the
 * base input matching its type, wired into the FormField/FormProvider error
 * path. Used by BookingForm for org-configured custom questions.
 */

import { memo, useCallback, useId, useMemo } from 'react';
import type { Key } from 'react-aria-components';

import type {
  CustomAnswerValue,
  CustomQuestion,
} from '@/lib/api/types/scheduling';
import {
  FormField,
  InputBase,
  InputGroup,
  Select,
  TextArea,
} from '@/lib/base/forms';
import { Checkbox, CheckboxGroup } from '@/lib/base/forms/checkbox';

import { customFieldKey } from './customQuestions';

// ============================================================================
// Types
// ============================================================================

export interface CustomQuestionFieldProps {
  question: CustomQuestion;
  value: CustomAnswerValue;
  onChange: (key: string, value: CustomAnswerValue) => void;
}

// ============================================================================
// Constants
// ============================================================================

const MULTI_SELECT_CAPTION = 'Select all that apply';

// ============================================================================
// Component
// ============================================================================

export const CustomQuestionField = memo(function CustomQuestionField({
  question,
  value,
  onChange,
}: CustomQuestionFieldProps) {
  const fieldName = customFieldKey(question.key);
  const multiSelectLabelId = useId();
  const checkboxErrorId = useId();
  const multiSelectErrorId = useId();

  const handleTextChange = useCallback(
    (v: string) => onChange(question.key, v),
    [onChange, question.key]
  );

  const handleSelectionChange = useCallback(
    (k: Key | null) => onChange(question.key, k ? String(k) : ''),
    [onChange, question.key]
  );

  const handleCheckboxChange = useCallback(
    (checked: boolean) => onChange(question.key, checked),
    [onChange, question.key]
  );

  const handleMultiSelectChange = useCallback(
    (vals: string[]) => onChange(question.key, vals),
    [onChange, question.key]
  );

  const selectItems = useMemo(
    () => (question.options ?? []).map(o => ({ id: o, label: o })),
    [question.options]
  );

  const multiSelectOptions = useMemo(
    () => question.options ?? [],
    [question.options]
  );

  const multiSelectValue = useMemo(
    () => (Array.isArray(value) ? value : []),
    [value]
  );

  switch (question.type) {
    case 'short_text':
    case 'phone':
      return (
        <FormField
          name={fieldName}
          label={question.label}
          isRequired={question.required}
        >
          {({ isInvalid, showError, inputId }) => (
            <InputGroup
              value={value as string}
              onChange={handleTextChange}
              isInvalid={isInvalid}
              showError={showError}
            >
              <InputBase
                id={inputId}
                type={question.type === 'phone' ? 'tel' : 'text'}
                placeholder={question.label}
                autoComplete={question.type === 'phone' ? 'tel' : undefined}
              />
            </InputGroup>
          )}
        </FormField>
      );

    case 'long_text':
      return (
        <FormField name={fieldName}>
          {({ isInvalid, showError }) => (
            <TextArea
              label={question.label}
              isRequired={question.required}
              value={value as string}
              onChange={handleTextChange}
              isInvalid={isInvalid}
              showError={showError}
              rows={3}
            />
          )}
        </FormField>
      );

    case 'single_select':
      return (
        <FormField name={fieldName}>
          {({ isInvalid, showError }) => (
            <Select
              label={question.label}
              isRequired={question.required}
              placeholder='Select an option'
              selectedKey={(value as string) || null}
              onSelectionChange={handleSelectionChange}
              items={selectItems}
              isInvalid={isInvalid}
              showError={showError}
            >
              {item => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          )}
        </FormField>
      );

    case 'checkbox':
      return (
        <FormField name={fieldName} errorId={checkboxErrorId}>
          {({ showError, error }) => (
            <Checkbox
              isSelected={value === true}
              isRequired={question.required}
              onChange={handleCheckboxChange}
              aria-describedby={
                showError && error ? checkboxErrorId : undefined
              }
              label={
                <span>
                  {question.label}
                  {question.required && (
                    <span
                      aria-hidden='true'
                      className='ml-0.5 text-[var(--color-text-error-primary)]'
                    >
                      *
                    </span>
                  )}
                </span>
              }
            />
          )}
        </FormField>
      );

    case 'multi_select':
      return (
        <FormField name={fieldName} errorId={multiSelectErrorId}>
          {({ isInvalid, showError, error }) => (
            <div className='space-y-1.5'>
              <div id={multiSelectLabelId}>
                <span className='text-sm font-medium text-[var(--color-text-primary)]'>
                  {question.label}
                  {question.required && (
                    <span
                      aria-hidden='true'
                      className='ml-0.5 text-[var(--color-text-error-primary)]'
                    >
                      *
                    </span>
                  )}
                </span>
                <p className='text-xs text-[var(--color-text-tertiary)]'>
                  {MULTI_SELECT_CAPTION}
                </p>
              </div>
              <CheckboxGroup
                aria-labelledby={multiSelectLabelId}
                aria-describedby={
                  showError && error ? multiSelectErrorId : undefined
                }
                value={multiSelectValue}
                onChange={handleMultiSelectChange}
                isRequired={question.required}
                isInvalid={isInvalid}
              >
                {multiSelectOptions.map(o => (
                  <Checkbox key={o} value={o} label={o} />
                ))}
              </CheckboxGroup>
            </div>
          )}
        </FormField>
      );

    default:
      return null;
  }
});
